import { useState, useEffect } from 'react'
import { useGameStore } from '../stores/gameStore'
import { Story } from '../types'
import Button from './ui/Button'
import LoadingSpinner from './ui/LoadingSpinner'

interface StoryViewerProps {
  story: Story
  onComplete?: () => void
  onWordPractice?: (word: string) => void
  className?: string
}

export default function StoryViewer({
  story,
  onComplete,
  onWordPractice,
  className = ''
}: StoryViewerProps) {
  const {
    gameState,
    nextPanel,
    previousPanel,
    setCurrentPanel
  } = useGameStore()
  
  const currentPanelIndex = gameState.currentPanel || 0

  const [isLoading] = useState(false)
  const [showTranscript, setShowTranscript] = useState(false)
  const [highlightedWords, setHighlightedWords] = useState<string[]>([])
  const [audioPlaying, setAudioPlaying] = useState(false)

  const currentPanel = story.comic_panels[currentPanelIndex] || story.comic_panels[0]
  const isFirstPanel = currentPanelIndex === 0
  const isLastPanel = currentPanelIndex === story.comic_panels.length - 1

  useEffect(() => {
    // Reset to first panel when story changes
    setCurrentPanel(0)
  }, [story.id, setCurrentPanel])

  useEffect(() => {
    // Extract target words from current panel
    if (currentPanel?.target_words) {
      setHighlightedWords(currentPanel.target_words)
    }
  }, [currentPanel])

  const handleNext = () => {
    if (isLastPanel) {
      onComplete?.()
    } else {
      nextPanel()
    }
  }

  const handlePrevious = () => {
    if (!isFirstPanel) {
      previousPanel()
    }
  }

  const handleWordClick = (word: string) => {
    if (highlightedWords.includes(word.toLowerCase())) {
      onWordPractice?.(word)
    }
  }

  const playAudio = async () => {
    if (!currentPanel?.audio_url || audioPlaying) return

    setAudioPlaying(true)
    try {
      const audio = new Audio(currentPanel.audio_url)
      audio.onended = () => setAudioPlaying(false)
      audio.onerror = () => setAudioPlaying(false)
      await audio.play()
    } catch (error) {
      console.error('Error playing audio:', error)
      setAudioPlaying(false)
    }
  }

  const renderTextWithHighlights = (text: string) => {
    if (!highlightedWords.length) return text

    let highlightedText = text
    highlightedWords.forEach(word => {
      const regex = new RegExp(`\\b${word}\\b`, 'gi')
      highlightedText = highlightedText.replace(regex, (match) =>
        `<span class="highlighted-word cursor-pointer bg-yellow-200 hover:bg-yellow-300 px-1 rounded transition-colors" data-word="${match}">${match}</span>`
      )
    })

    return (
      <div
        dangerouslySetInnerHTML={{ __html: highlightedText }}
        onClick={(e) => {
          const target = e.target as HTMLElement
          if (target.classList.contains('highlighted-word')) {
            const word = target.getAttribute('data-word')
            if (word) handleWordClick(word)
          }
        }}
      />
    )
  }

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center h-96 ${className}`}>
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className={`bg-white rounded-lg shadow-lg overflow-hidden ${className}`}>
      {/* Story Header */}
      <div className="bg-gradient-to-r from-primary-500 to-primary-600 text-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">{story.title}</h2>
            <p className="text-primary-100 text-sm">
              Panel {currentPanelIndex + 1} of {story.comic_panels.length}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {currentPanel?.audio_url && (
              <Button
                onClick={playAudio}
                disabled={audioPlaying}
                variant="outline"
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                {audioPlaying ? (
                  <LoadingSpinner size="sm" color="white" />
                ) : (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </Button>
            )}
            <Button
              onClick={() => setShowTranscript(!showTranscript)}
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </Button>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-gray-200 h-1">
        <div
          className="bg-primary-500 h-1 transition-all duration-300"
          style={{ width: `${((currentPanelIndex + 1) / story.comic_panels.length) * 100}%` }}
        />
      </div>

      {/* Panel Content */}
      <div className="p-6">
        {/* Panel Image */}
        {currentPanel?.image_url && (
          <div className="mb-6">
            <img
              src={currentPanel.image_url}
              alt={`Panel ${currentPanelIndex + 1}`}
              className="w-full h-64 object-cover rounded-lg shadow-sm"
              onError={(e) => {
                const target = e.target as HTMLImageElement
                target.style.display = 'none'
              }}
            />
          </div>
        )}

        {/* Panel Text */}
        <div className="mb-6">
          <div className="text-lg leading-relaxed text-gray-800">
            {renderTextWithHighlights(currentPanel?.text || '')}
          </div>

          {/* Transcript Toggle */}
          {showTranscript && currentPanel?.transcript && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
              <h4 className="font-semibold text-gray-900 mb-2">Transcript</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                {currentPanel.transcript}
              </p>
            </div>
          )}
        </div>

        {/* Target Words */}
        {highlightedWords.length > 0 && (
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 mb-3">Practice Words</h4>
            <div className="flex flex-wrap gap-2">
              {highlightedWords.map((word, index) => (
                <button
                  key={index}
                  onClick={() => handleWordClick(word)}
                  className="px-3 py-1 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 rounded-full text-sm font-medium transition-colors cursor-pointer"
                >
                  {word}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Panel Notes */}
        {currentPanel?.notes && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Learning Notes</h4>
            <p className="text-sm text-blue-800">
              {currentPanel.notes}
            </p>
          </div>
        )}
      </div>

      {/* Navigation Controls */}
      <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t">
        <Button
          onClick={handlePrevious}
          disabled={isFirstPanel}
          variant="outline"
          className="flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>Previous</span>
        </Button>

        <div className="flex items-center space-x-2">
          {story.comic_panels.map((_, index: number) => (
            <button
              key={index}
              onClick={() => setCurrentPanel(index)}
              className={`w-3 h-3 rounded-full transition-colors ${
                index === currentPanelIndex
                  ? 'bg-primary-500'
                  : index < currentPanelIndex
                  ? 'bg-green-400'
                  : 'bg-gray-300'
              }`}
              title={`Panel ${index + 1}`}
            />
          ))}
        </div>

        <Button
          onClick={handleNext}
          className="flex items-center space-x-2"
        >
          <span>{isLastPanel ? 'Complete' : 'Next'}</span>
          {isLastPanel ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
        </Button>
      </div>

      {/* Story Completion */}
      {isLastPanel && (
        <div className="bg-green-50 border-t border-green-200 p-4">
          <div className="flex items-center space-x-3">
            <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="font-semibold text-green-900">Story Complete!</h4>
              <p className="text-sm text-green-700">
                Great job! You&#39;ve finished reading &quot;{story.title}&quot;.
                {highlightedWords.length > 0 && (
                  <span> Practice the highlighted words to improve your pronunciation.</span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}